import {
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import MenuItem from '../../../shared/interfaces/menuItems.interface';
import { FilterBarComponent } from '../filter-bar/filter-bar.component';
import { DynamicTeaserShowcaseComponent } from '../dynamic-teaser-showcase/dynamic-teaser-showcase.component';
import { SectionComponent } from '../section/section.component';
import { NotFoundBoxComponent } from '../../molecules/not-found-box/not-found-box.component';
import { AlertComponent } from '../../molecules/alert/alert.component';
import { BreadcrumbsComponent } from '../../molecules/breadcrumb/breadcrumb.component';
import { NewstickerComponent } from '../../molecules/newsticker/newsticker.component';
import { InpageNavigationComponent } from '../inpage-navigation/inpage-navigation.component';
import { CommonModule } from '@angular/common';
import { ImageMetaData } from '../../../shared/interfaces/meta-data.interface';
import { News } from '../../../shared/interfaces/news.interface';
import {
  CaasComponent,
  CaasDisruptor,
  CaasError404,
  CaasInpageNav,
  CaasInternalLink,
  CaasLayout,
  CaasMainSection,
  CaasStage,
  Caasnewsticker,
} from '../../../shared/interfaces/caas/caas.interface';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { ImagesService } from '../../../services/images.service';
import {
  TextBlockAlign,
  TextBlockTitleVariant,
} from '../../../shared/enums/text-block-type.enums';
import { TextBlockComponent } from '../../atoms/text-block/text-block.component';
import { HeaderStageComponent } from '../header-stage/header-stage.component';
import { InpageSearchComponent } from '../inpage-search/inpage-search.component';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';

@Component({
  selector: 'hdi-stage',
  standalone: true,
  templateUrl: './stage.component.html',
  imports: [
    CommonModule,
    FilterBarComponent,
    DynamicTeaserShowcaseComponent,
    SectionComponent,
    NotFoundBoxComponent,
    AlertComponent,
    BreadcrumbsComponent,
    NewstickerComponent,
    InpageNavigationComponent,
    TextBlockComponent,
    HeaderStageComponent,
    InpageSearchComponent,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class StageComponent implements OnChanges {
  @Input() caasData: CaasLayout[] = [];
  @Input() breadcrumbs: { title: string; url?: string }[] = [];
  @Input() error404?: CaasError404;
  @Input() newsComponent?: CaasComponent;
  @Input() news: News[] = [];
  @Input() newsFilters: {
    filter: string;
    categories: string[];
    identifier: string;
  }[] = [];
  @Input() imagesMetaData: ImageMetaData[] = [];
  @Input() filteredNews: News[] = [];
  @Input() inpageMenu: MenuItem[] = [];
  @Output() updateNews: EventEmitter<News[]> = new EventEmitter<News[]>();

  public buehne?: CaasStage;
  public welcome?: CaasStage;
  public homeStage?: CaasStage;
  public alert?: CaasDisruptor;
  public newsticker?: Caasnewsticker;
  public inpageNav?: CaasInpageNav;

  public alternateBackground: ('white' | 'grey')[] = [];
  public paddingTop = false;
  public paddingBlock = false;
  public paddingBottom = false;

  public welcomeContent?: {
    header: string;
    body: string;
    search?: {
      hasSearch?: boolean;
      placeholder?: string;
    };
  };

  public TextBlockTitleVariant = TextBlockTitleVariant;
  public TextBlockAlign = TextBlockAlign;

  constructor(
    private readonly caasBodytextService: CaasBodytextService,
    private readonly imagesService: ImagesService,
    private readonly errorHandler: ErrorHandlerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setLayouts();
    }
    if (changes['imagesMetaData']) {
      this.imagesService.setImagesMetaData(
        changes['imagesMetaData'].currentValue
      );
    }

    this.getAlternateBackground();
  }

  setLayouts() {
    let layout: CaasLayout | undefined;
    let disruptor: CaasLayout | undefined;
    let stage: CaasMainSection | CaasStage | undefined;
    let uid: string | undefined;

    safeAssign(
      () => {
        layout = this.caasData.find((layout) => layout.name === 'stage');
      },
      'setLayouts.layout',
      this.errorHandler
    );

    safeAssign(
      () => {
        disruptor = this.caasData.find((layout) => layout.name === 'disruptor');
      },
      'setLayouts.disruptor',
      this.errorHandler
    );

    safeAssign(
      () => {
        stage = layout?.children?.[0];
      },
      'setLayouts.stage',
      this.errorHandler
    );

    safeAssign(
      () => {
        uid = stage?.template.uid;
      },
      'setLayouts.uid',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (uid) {
          if (uid === 'st_stage' || uid === 'st_stage_temp') {
            this.buehne = stage as CaasStage;
          } else if (uid === 'st_welcome') {
            this.welcome = stage as CaasStage;
            this.setWelcome();
          }
        }
      },
      'setLayouts.buehneOrWelcome',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.alert = disruptor?.children?.find(
          (el) => el.template.uid === 'st_disruptor'
        ) as CaasDisruptor;
      },
      'setLayouts.alert',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.newsticker = disruptor?.children?.find(
          (el) => el.template.uid === 'st_ticker'
        ) as Caasnewsticker;
      },
      'setLayouts.newsticker',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.setInPageNav();
      },
      'setLayouts.setInPageNav',
      this.errorHandler
    );
  }

  setWelcome() {
    safeAssign(
      () => {
        this.welcomeContent = {
          header: this.welcome?.formData?.st_header?.value ?? '',
          body: this.caasBodytextService.extractTextEditorContent(
            this.welcome?.formData?.st_bodytext?.value ?? ''
          ),
        };
      },
      'setWelcome.welcomeContent (base)',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (this.welcome?.formData?.st_inpagesearch?.value) {
          if (this.welcomeContent) {
            this.welcomeContent = {
              ...this.welcomeContent,
              search: { hasSearch: true },
            };
          }
        }
      },
      'setWelcome.inPageSearchFlag',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (this.welcome?.formData?.st_inpagesearch_placeholder?.value) {
          if (this.welcomeContent) {
            this.welcomeContent = {
              ...this.welcomeContent,
              search: {
                ...this.welcomeContent.search,
                placeholder:
                  this.welcome?.formData?.st_inpagesearch_placeholder?.value,
              },
            };
          }
        }
      },
      'setWelcome.inPageSearchPlaceholder',
      this.errorHandler
    );
  }

  setInPageNav() {
    let inpageNavLayout: CaasLayout | undefined;

    safeAssign(
      () => {
        inpageNavLayout = this.caasData.find(
          (layout) => layout.name === 'inpagenav'
        );
      },
      'setInPageNav.findLayout',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (inpageNavLayout?.children) {
          this.inpageNav = inpageNavLayout.children[0] as CaasInpageNav;
        }
      },
      'setInPageNav.assignInpageNav',
      this.errorHandler
    );
  }
  getInternalUrl(internalLinkValue: CaasInternalLink['value']): string {
    if (internalLinkValue && internalLinkValue.uid === 'startseite') {
      return '/';
    } else {
      return '/404';
    }
  }

  getAlternateBackground() {
    let isWhite: (index: number) => boolean;
    safeAssign(
      () => {
        isWhite = (index: number) => index % 2 !== 0;
      },
      'getAlternateBackground.initIsWhite',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.caasData.forEach((section, index) => {
          if (section['formData']?.st_background_reset?.value) {
            // Force white and flip the alternation rule
            this.alternateBackground.push('white');
            isWhite = isWhite(0) ? (i) => i % 2 === 0 : (i) => i % 2 !== 0;
          } else {
            // Use the current alternation rule
            this.alternateBackground.push(isWhite(index) ? 'white' : 'grey');
          }
        });
      },
      'getAlternateBackground.populateAlternateBackground',
      this.errorHandler
    );
  }

  onFilteredNews(newsArray: News[]): void {
    this.updateNews.emit(newsArray);
  }
}
